import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faSave, faCheck } from '@fortawesome/free-solid-svg-icons';
import { Input, SaveButton, Checkbox } from './styled';

const ControlManagement = ({ tableName }) => {
  const controlTypeOptions = [
    'NumberBox',
    'TextBox',
    'SelectBox',
    'CheckBox',
    'ArrayInput',
    'ArraySelectBox',
    'PasswordBox',
    'DateTimeBox',
    'TimeBox',
    'DateBox',
    'ArrayButton',
    'TextareaBox',
    'MultiplyColumn',
    'Button',
    'FileUpload',
  ];
  
  const bindTypeOptions = ['M', 'S', 'O', 'C'];

  const [controlData, setControlData] = useState([]);
  const [newRow, setNewRow] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [columnIdOptions, setColumnIdOptions] = useState([]);

  // 데이터 조회
  useEffect(() => {
    if (tableName) {
      fetchControlData();
      fetchColumnIds();
    }
  }, [tableName]);

  const fetchControlData = async () => {
    try {
      const response = await fetch(`/request/getControlInfo?boardId=${tableName}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setControlData(data.map((item) => ({ ...item, bindKeyOptions: [] })));
    } catch (error) {
      console.error('Error fetching control data:', error);
    }
  };

  const fetchColumnIds = async () => {
    try {
      const response = await fetch(`/request/getColumns?boardId=${tableName}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setColumnIdOptions(data.map((column) => column.column_name));
    } catch (error) {
      console.error('Error fetching column IDs:', error);
    }
  };

  const getCommonCodeGroup = async (index) => {
    try {
      const res = await fetch('/request/getCommonCodeGroup', {
        credentials: 'include'
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        setControlData((prevData) =>
          prevData.map((item, idx) =>
            idx === index ? { ...item, bindKeyOptions: data } : item
          )
        );
      }
    } catch (error) {
      console.error('Error fetching common code group:', error);
    }
  };

  const getTableList = async (index) => {
    try {
      const res = await fetch('/request/getTableList', {
        credentials: 'include'
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        setControlData((prevData) =>
          prevData.map((item, idx) =>
            idx === index ? { ...item, bindKeyOptions: data } : item
          )
        );
      }
    } catch (error) {
      console.error('Error fetching table list:', error);
    }
  };

  const handleAddRow = () => {
    setNewRow({
      board_id: tableName,
      column_id: '',
      control_type: '',
      control_size: 2,
      bind_key: '',
      bind_type: '',
      bind_option: '',
      bind_display: '',
      root_column: '',
      where_column: '',
      bindKeyOptions: [],
    });
    setIsEditing(true);
  };

  const handleInputChange = (e, index, field) => {
    const newData = controlData.map((row, i) => {
      if (i === index) {
        return { ...row, [field]: e.target.value };
      }
      return row;
    });
    setControlData(newData);
  };

  const handleNewRowInputChange = (e, field) => {
    setNewRow({
      ...newRow,
      [field]: e.target.value,
    });
  };

  const handleTypeChange = (e, index) => {
    handleInputChange(e, index, 'bind_type');
    if (e.target.value === 'M') {
      getTableList(index);
    } else {
      getCommonCodeGroup(index);
    }
  };

  const handleNewRowTypeChange = (e) => {
    handleNewRowInputChange(e, 'bind_type');
    // 새 행에 대한 bind key options 설정 로직 추가 필요
  };

  const handleSaveNewRow = () => {
    setControlData([...controlData, newRow]);
    setNewRow(null);
    setIsEditing(false);
  };

  const handleSelectRow = (index) => {
    setSelectedRows((prevSelectedRows) => {
      if (prevSelectedRows.includes(index)) {
        return prevSelectedRows.filter((row) => row !== index);
      } else {
        return [...prevSelectedRows, index];
      }
    });
  };

  const handleSave = async () => {
    try {
      const response = await fetch('/request/updateControlInfo', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(controlData),
      });
      
      if (response.ok) {
        alert('컨트롤 설정이 성공적으로 저장되었습니다!');
        setIsEditing(false);
        setSelectedRows([]);
        fetchControlData();
      }
    } catch (error) {
      console.error('Error saving control data:', error);
      alert('저장 중 오류가 발생했습니다.');
    }
  };

  const handleDeleteRows = async () => {
    const rowsToDelete = selectedRows.map((index) => controlData[index]);
    
    try {
      const response = await fetch('/request/deleteControlInfo', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(rowsToDelete),
      });
      
      if (response.ok) {
        alert('선택된 컨트롤이 삭제되었습니다!');
        fetchControlData();
        setSelectedRows([]);
      }
    } catch (error) {
      console.error('Error deleting control data:', error);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const toggleEdit = () => {
    if (newRow) {
      setNewRow(null);
    }
    setIsEditing(!isEditing);
  };

  return (
    <div style={{ padding: '20px', backgroundColor: 'white' }}>
      {/* 컨트롤 버튼들 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '20px',
        padding: '16px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        <h4 style={{ margin: 0, color: '#495057' }}>
          🎮 {tableName} 컨트롤 설정
        </h4>
        <div style={{ display: 'flex', gap: '8px' }}>
          {!newRow && (
            <SaveButton
              onClick={handleAddRow}
              style={{
                backgroundColor: '#28a745',
                padding: '8px 16px',
                fontSize: '13px',
                width: 'auto',
                margin: 0
              }}
            >
              <FontAwesomeIcon icon={faPlus} /> 추가
            </SaveButton>
          )}
          <SaveButton
            onClick={toggleEdit}
            disabled={selectedRows.length === 0 && !newRow}
            style={{
              backgroundColor: isEditing ? '#6c757d' : '#007bff',
              padding: '8px 16px',
              fontSize: '13px',
              width: 'auto',
              margin: 0
            }}
          >
            {isEditing ? '취소' : '수정'}
          </SaveButton>
          <SaveButton
            onClick={handleDeleteRows}
            disabled={selectedRows.length === 0}
            style={{
              backgroundColor: '#dc3545',
              padding: '8px 16px',
              fontSize: '13px',
              width: 'auto',
              margin: 0
            }}
          >
            <FontAwesomeIcon icon={faTrash} /> 삭제
          </SaveButton>
          <SaveButton
            onClick={handleSave}
            style={{
              backgroundColor: '#17a2b8',
              padding: '8px 16px',
              fontSize: '13px',
              width: 'auto',
              margin: 0
            }}
          >
            <FontAwesomeIcon icon={faSave} /> 저장
          </SaveButton>
        </div>
      </div>

      {/* 컨트롤 테이블 */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '12px',
          backgroundColor: 'white',
          border: '1px solid #dee2e6'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#e9ecef' }}>
              <th style={{ padding: '12px 8px', border: '1px solid #dee2e6', width: '40px' }}>선택</th>
              <th style={{ padding: '12px 8px', border: '1px solid #dee2e6' }}>컬럼 ID</th>
              <th style={{ padding: '12px 8px', border: '1px solid #dee2e6' }}>컨트롤 타입</th>
              <th style={{ padding: '12px 8px', border: '1px solid #dee2e6' }}>바인드 타입</th>
              <th style={{ padding: '12px 8px', border: '1px solid #dee2e6' }}>바인드 키</th>
              <th style={{ padding: '12px 8px', border: '1px solid #dee2e6' }}>바인드 옵션</th>
              <th style={{ padding: '12px 8px', border: '1px solid #dee2e6' }}>바인드 표시</th>
              <th style={{ padding: '12px 8px', border: '1px solid #dee2e6' }}>루트 컬럼</th>
              <th style={{ padding: '12px 8px', border: '1px solid #dee2e6' }}>조건 컬럼</th>
            </tr>
          </thead>
          <tbody>
            {controlData.map((control, index) => (
              <tr key={index} style={{ backgroundColor: selectedRows.includes(index) ? '#e3f2fd' : 'white' }}>
                <td style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                  {!newRow && (
                    <Checkbox
                      type="checkbox"
                      checked={selectedRows.includes(index)}
                      onChange={() => handleSelectRow(index)}
                      disabled={isEditing && !selectedRows.includes(index)}
                    />
                  )}
                </td>
                {isEditing && selectedRows.includes(index) ? (
                  <>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                      <select
                        value={control.column_id}
                        onChange={(e) => handleInputChange(e, index, 'column_id')}
                        style={{
                          width: '100%',
                          padding: '4px',
                          border: '1px solid #ced4da',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}
                      >
                        <option value="">선택</option>
                        {columnIdOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                      <select
                        value={control.control_type}
                        onChange={(e) => handleInputChange(e, index, 'control_type')}
                        style={{
                          width: '100%',
                          padding: '4px',
                          border: '1px solid #ced4da',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}
                      >
                        <option value="">선택</option>
                        {controlTypeOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                      <select
                        value={control.bind_type}
                        onChange={(e) => handleTypeChange(e, index)}
                        style={{
                          width: '100%',
                          padding: '4px',
                          border: '1px solid #ced4da',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}
                      >
                        <option value="">선택</option>
                        {bindTypeOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                      <select
                        value={control.bind_key}
                        onChange={(e) => handleInputChange(e, index, 'bind_key')}
                        style={{
                          width: '100%',
                          padding: '4px',
                          border: '1px solid #ced4da',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}
                      >
                        <option value="">선택</option>
                        {control.bindKeyOptions.map((option) => (
                          <option key={option.std_group_code} value={option.std_group_code}>
                            {option.std_group_code}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                      <Input
                        value={control.bind_option}
                        onChange={(e) => handleInputChange(e, index, 'bind_option')}
                        style={{ margin: 0, fontSize: '12px', padding: '4px' }}
                      />
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                      <Input
                        value={control.bind_display}
                        onChange={(e) => handleInputChange(e, index, 'bind_display')}
                        style={{ margin: 0, fontSize: '12px', padding: '4px' }}
                      />
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                      <Input
                        value={control.root_column}
                        onChange={(e) => handleInputChange(e, index, 'root_column')}
                        style={{ margin: 0, fontSize: '12px', padding: '4px' }}
                      />
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                      <Input
                        value={control.where_column}
                        onChange={(e) => handleInputChange(e, index, 'where_column')}
                        style={{ margin: 0, fontSize: '12px', padding: '4px' }}
                      />
                    </td>
                  </>
                ) : (
                  <>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>{control.column_id}</td>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>{control.control_type}</td>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>{control.bind_type}</td>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>{control.bind_key}</td>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>{control.bind_option}</td>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>{control.bind_display}</td>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>{control.root_column}</td>
                    <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>{control.where_column}</td>
                  </>
                )}
              </tr>
            ))}
            {newRow && (
              <tr style={{ backgroundColor: '#f0f8ff' }}>
                <td style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                  <FontAwesomeIcon
                    icon={faCheck}
                    onClick={handleSaveNewRow}
                    style={{ cursor: 'pointer', color: '#28a745' }}
                  />
                </td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                  <select
                    value={newRow.column_id}
                    onChange={(e) => handleNewRowInputChange(e, 'column_id')}
                    style={{
                      width: '100%',
                      padding: '4px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                  >
                    <option value="">선택</option>
                    {columnIdOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                  <select
                    value={newRow.control_type}
                    onChange={(e) => handleNewRowInputChange(e, 'control_type')}
                    style={{
                      width: '100%',
                      padding: '4px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                  >
                    <option value="">선택</option>
                    {controlTypeOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                  <select
                    value={newRow.bind_type}
                    onChange={(e) => handleNewRowTypeChange(e)}
                    style={{
                      width: '100%',
                      padding: '4px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                  >
                    <option value="">선택</option>
                    {bindTypeOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                  <select
                    value={newRow.bind_key}
                    onChange={(e) => handleNewRowInputChange(e, 'bind_key')}
                    style={{
                      width: '100%',
                      padding: '4px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                  >
                    <option value="">선택</option>
                    {newRow.bindKeyOptions.map((option) => (
                      <option key={option.std_group_code} value={option.std_group_code}>
                        {option.std_group_code}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                  <Input
                    value={newRow.bind_option}
                    onChange={(e) => handleNewRowInputChange(e, 'bind_option')}
                    style={{ margin: 0, fontSize: '12px', padding: '4px' }}
                  />
                </td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                  <Input
                    value={newRow.bind_display}
                    onChange={(e) => handleNewRowInputChange(e, 'bind_display')}
                    style={{ margin: 0, fontSize: '12px', padding: '4px' }}
                  />
                </td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                  <Input
                    value={newRow.root_column}
                    onChange={(e) => handleNewRowInputChange(e, 'root_column')}
                    style={{ margin: 0, fontSize: '12px', padding: '4px' }}
                  />
                </td>
                <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                  <Input
                    value={newRow.where_column}
                    onChange={(e) => handleNewRowInputChange(e, 'where_column')}
                    style={{ margin: 0, fontSize: '12px', padding: '4px' }}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {controlData.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#6c757d',
          fontStyle: 'italic'
        }}>
          설정된 컨트롤이 없습니다. 새 컨트롤을 추가해보세요.
        </div>
      )}
    </div>
  );
};

export default ControlManagement;